import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { userAPI } from '../lib/api';

export interface CitizenProfile {
  id: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  zipCode?: string | null;
  politicalAffiliation?: string | null;
  interests?: string[] | null;
  houseDistrict?: string | null;
  senateDistrict?: string | null;
  city?: string | null;
  state?: string | null;
  profileImageUrl?: string | null;
}

/**
 * Coerce the backend's `interests` into a real string[]. The users.interests
 * column is TEXT, and the API returns it however Postgres serialized the array
 * it was given — most often the array-literal string `{"education","healthcare"}`,
 * sometimes a comma list or a JSON string. The citizen UI treats interests as an
 * array (chip toggles, priority chips), so normalize it once here so every
 * consumer — and the round-trip save — works regardless of the stored shape.
 */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value !== 'string') return [];
  const s = value.trim();
  if (!s) return [];
  // Postgres array literal: {"a","b"} or {a,b}
  if (s.startsWith('{') && s.endsWith('}')) {
    const inner = s.slice(1, -1).trim();
    if (!inner) return [];
    return inner
      .split(',')
      .map((x) => x.trim().replace(/^"(.*)"$/, '$1'))
      .filter(Boolean);
  }
  // JSON array string: ["a","b"]
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v));
    } catch {
      /* fall through to comma split */
    }
  }
  // Comma-separated fallback
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

export function useProfile() {
  return useQuery<CitizenProfile>({
    queryKey: ['citizen-profile'],
    queryFn: async () => {
      const { data } = await userAPI.getProfile();
      const profile = (data.data ?? {}) as CitizenProfile;
      return { ...profile, interests: toStringArray(profile.interests) };
    },
  });
}

export interface CalendarEvent {
  calendar_id: string;
  reading_title: string;
  reading_date: string;
  reading_time?: string | null;
  chamber?: string | null;
  committee_flag?: boolean;
  derived_location?: string | null;
  bills: Array<{ number: string; id?: string; title?: string; status?: string }>;
}

// This week's legislative calendar (public endpoint)
export function useWeekCalendar() {
  return useQuery<CalendarEvent[]>({
    queryKey: ['citizen-week-calendar'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await api.get(`/public/calendar/all/week/${today}`);
      const events = data.data?.events;
      // A non-array (error envelope, HTML from the SPA rewrite) must not reach
      // buildFeed's .map (Sentry FRONTEND-21, #1451).
      return Array.isArray(events) ? events : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// The citizen's personal workspace id, resolved BY IDENTITY server-side
// (Citizens-org-owned, owner-membered) — never "first of my workspaces": for
// a dual-access user that list includes shared pro workspaces, and whichever
// sorted first became the citizen tracking target
// (PRO_CITIZEN_DUAL_ACCESS_PRD §5.5). The cached value is revalidated once
// per session so a stale id pointing at a pro workspace self-heals.
const REVALIDATED_FLAG = 'citizenWorkspaceIdRevalidated';

export function useCitizenWorkspaceId() {
  const [workspaceId, setWorkspaceId] = useState<string | null>(localStorage.getItem('citizenWorkspaceId'));

  useEffect(() => {
    if (workspaceId && sessionStorage.getItem(REVALIDATED_FLAG)) return;
    api
      .get('/workspace-bill-tracking/personal-workspace')
      .then(({ data }) => {
        sessionStorage.setItem(REVALIDATED_FLAG, '1');
        const ws = data.data;
        if (ws?.id) {
          if (ws.id !== workspaceId) {
            setWorkspaceId(ws.id);
            localStorage.setItem('citizenWorkspaceId', ws.id);
          }
        } else {
          // No personal workspace resolvable — clear rather than guess.
          setWorkspaceId(null);
          localStorage.removeItem('citizenWorkspaceId');
        }
      })
      .catch(() => {});
  }, [workspaceId]);

  return workspaceId;
}

export interface OutreachItem {
  id: string;
  name: string;
  description?: string | null;
  type?: string | null;
  status?: string | null;
  closes_at?: string | null;
  end_date?: string | null;
  template_name?: string | null;
  response_count?: number;
  updated_at?: string;
}

// Outreach campaigns (surveys / RSVPs) visible to the citizen's workspace.
// Named to avoid colliding with the pro workspace tab's useOutreachItems hook,
// which has a different return shape.
export function useCitizenOutreachItems(workspaceId: string | null) {
  return useQuery<OutreachItem[]>({
    queryKey: ['citizen-outreach', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data } = await api.get(`/outreach-unified/workspaces/${workspaceId}/items`);
      const items = data.data;
      return Array.isArray(items) ? items : [];
    },
    enabled: !!workspaceId,
  });
}

export interface NewsItem {
  title: string;
  link?: string;
  pubDate?: string;
  source?: string;
  description?: string;
}

export function useNews() {
  return useQuery<NewsItem[]>({
    queryKey: ['citizen-news'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/rss_news_feed/data');
      const items = data.data?.items || data.data || [];
      return Array.isArray(items) ? items : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Tracked bills in the citizen's workspace
export function useTrackedBills(workspaceId: string | null) {
  return useQuery({
    queryKey: ['citizen-tracked-bills', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data } = await api.get(`/workspace-bill-tracking/workspace/${workspaceId}`);
      const result = data.data;
      if (Array.isArray(result)) return result;
      if (result && typeof result === 'object') {
        const arr = result.bills || result.items;
        if (Array.isArray(arr)) return arr;
      }
      return [];
    },
    enabled: !!workspaceId,
  });
}
